import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import get from 'lodash.get';

// components
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';

// styles
import styles from './PdpRespSimilarItem.scss';

// constants
import { VERTICAL_OFFSET_RESP_IMG, SRC_SET_SIZES_TILE } from '../../../constants/imageConstants';

function PdpRespSimilarItemComponent({
    index,
    item,
    onClick,
    user,
    quickView,
    favorites,
    currency,
    lazyLoadStartIndex,
    showLightBox,
    viewer,
    isMobile, // isMobile added to allow component to get mobile quickview styles
    showMeasurements,
}) {
    const { activeQuickView, openQuickView, closeQuickView } = quickView || {};
    const showQuickView =
        !!get(viewer, 'isSeoQuicklookEnabled') && !!openQuickView && !!closeQuickView;
    const { canShowSellerName } = user || {};
    const canShowCreatorName = !canShowSellerName && item.isFurniture;

    // Favoriting
    let favoritesComponent = false;
    if (!!get(viewer, 'isFavoritesEnabled') && favorites) {
        favoritesComponent = (
            <div className={styles.favorites}>
                <FavoritesItem
                    wrapperClass={styles.favoritesWrapper}
                    heartIconWrapperClass={styles.favoritesIconWrapper}
                    folderIconWrapperClass={styles.favoritesIconWrapper}
                    itemId={item.serviceId}
                    {...favorites}
                    itemTitle={item.title || ''}
                    itemImage={item.firstPhotoWebPath || ''}
                    isVerifiedTrade={get(user, 'isVerifiedTrade') || false}
                />
            </div>
        );
    }

    // Default Layout
    return (
        <div className={styles.item}>
            <ItemTile
                index={index}
                item={item}
                currency={currency}
                itemsPerRow={lazyLoadStartIndex}
                showSellerName={canShowSellerName}
                showCreatorName={canShowCreatorName}
                onClick={() => onClick(index)}
                activeQuickView={activeQuickView}
                openQuickView={openQuickView}
                closeQuickView={closeQuickView}
                showQuickView={showQuickView}
                showLightBox={showLightBox}
                disableBorder
                useLoFiLazyLoader
                isMobile={isMobile}
                imageSize="imageSmall"
                itemSearch={null}
                imageLoadVerticalOffset={VERTICAL_OFFSET_RESP_IMG}
                viewer={null}
                srcSetSizes={SRC_SET_SIZES_TILE}
                showMSKUSwatch={false}
                favoritesComponent={favoritesComponent}
                showMeasurements={showMeasurements}
            />
        </div>
    );
}

PdpRespSimilarItemComponent.defaultProps = {
    lazyLoadStartIndex: 0,
    showLightBox: false,
    isMobile: false,
};

PdpRespSimilarItemComponent.propTypes = {
    index: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    currency: PropTypes.string,
    onClick: PropTypes.func,
    // @see QuickViewProvider.jsx
    quickView: PropTypes.shape({
        openQuickView: PropTypes.func,
        closeQuickView: PropTypes.func,
        activeQuickView: PropTypes.number,
    }),
    // @see PdpRespFavoritesProvider.jsx
    favorites: PropTypes.shape({
        location: PropTypes.string.isRequired,
        userId: PropTypes.string,
        userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
        itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        disable: PropTypes.bool,
        environment: PropTypes.object.isRequired,
        singlePortfolioItem: PropTypes.bool.isRequired,
        loadPortfolioItemData: PropTypes.bool.isRequired,
        heartViewer: PropTypes.object,
        folderViewer: PropTypes.object,
    }),
    lazyLoadStartIndex: PropTypes.number,
    user: PropTypes.object,
    showLightBox: PropTypes.bool,
    viewer: PropTypes.object,
    isMobile: PropTypes.bool,
    showMeasurements: PropTypes.bool,
};

export const PdpRespSimilarItem = createFragmentContainer(PdpRespSimilarItemComponent, {
    item: graphql`
        fragment PdpRespSimilarItem_item on Item {
            serviceId
            isFurniture
            ...ItemTile_item @arguments(isTrade: $isTrade)
        }
    `,
    user: graphql`
        fragment PdpRespSimilarItem_user on User {
            canShowSellerName
            isVerifiedTrade
        }
    `,
    viewer: graphql`
        fragment PdpRespSimilarItem_viewer on Viewer {
            isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
            isFavoritesEnabled: isEnabledByLocale(featureName: "FAVORITES")
        }
    `,
});
